import { useForm } from '@inertiajs/react'
import Layout from '../../components/Layout'
import { sortPeople } from '../../utils/peopleSort'

interface Props {
  org_members: { id: number; full_name: string }[]
  teams: { id: number; name: string }[]
  rocks: { id: number; title: string }[]
  templates: { id: number; name: string }[]
  current_user_id: number
}

export default function NewProject({ org_members, teams, rocks, templates, current_user_id }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    project: {
      name: '',
      description: '',
      owner_id: current_user_id,
      linked_rock_id: '' as number | '',
      progress_type: 'manual',
      health: 'on_track'
    },
    template_id: '' as number | '',
    team_link_ids: [] as number[],
    initial_member_ids: [] as number[]
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/projects')
  }

  function toggleTeam(id: number) {
    setData('team_link_ids', data.team_link_ids.includes(id) ? data.team_link_ids.filter((t) => t !== id) : [...data.team_link_ids, id])
  }
  function toggleMember(id: number) {
    setData('initial_member_ids', data.initial_member_ids.includes(id) ? data.initial_member_ids.filter((u) => u !== id) : [...data.initial_member_ids, id])
  }

  return (
    <Layout>
      <h1 className="text-3xl font-semibold mb-6" style={{ color: '#1B1B1B' }}>New Project</h1>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-5 max-w-3xl">
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Name</label>
          <input type="text" required value={data.project.name} onChange={(e) => setData('project', { ...data.project, name: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
          {errors['project.name'] && <p className="text-xs text-[#ED1C24] mt-1">{errors['project.name']}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
          <textarea rows={3} value={data.project.description} onChange={(e) => setData('project', { ...data.project, description: e.target.value })} className="w-full border rounded-lg px-3 py-2" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Owner</label>
            <select value={data.project.owner_id} onChange={(e) => setData('project', { ...data.project, owner_id: Number(e.target.value) })} className="w-full border rounded-lg px-3 py-2">
              {sortPeople(org_members).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Start from template (optional)</label>
            <select value={data.template_id} onChange={(e) => setData('template_id', e.target.value ? Number(e.target.value) : '')} className="w-full border rounded-lg px-3 py-2">
              <option value="">Blank project</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Link to Rock (optional)</label>
            <select value={data.project.linked_rock_id} onChange={(e) => setData('project', { ...data.project, linked_rock_id: e.target.value ? Number(e.target.value) : '' })} className="w-full border rounded-lg px-3 py-2">
              <option value="">None</option>
              {rocks.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Progress mode</label>
            <select value={data.project.progress_type} onChange={(e) => setData('project', { ...data.project, progress_type: e.target.value })} className="w-full border rounded-lg px-3 py-2">
              <option value="manual">Manual updates</option>
              <option value="from_to_dos">Auto from to-dos</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Team spaces (optional)</label>
          <div className="flex flex-wrap gap-2">
            {teams.map((t) => (
              <button type="button" key={t.id} onClick={() => toggleTeam(t.id)} className={`px-3 py-1.5 text-xs rounded-full border ${data.team_link_ids.includes(t.id) ? 'bg-[#351E1D] text-white border-[#351E1D]' : 'bg-white text-text-secondary'}`}>
                {t.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Initial collaborators</label>
          <div className="flex flex-wrap gap-2">
            {sortPeople(org_members.filter((u) => u.id !== data.project.owner_id)).map((u) => (
              <button type="button" key={u.id} onClick={() => toggleMember(u.id)} className={`px-3 py-1.5 text-xs rounded-full border ${data.initial_member_ids.includes(u.id) ? 'bg-[#15532E] text-white border-[#15532E]' : 'bg-white text-text-secondary'}`}>
                {u.full_name}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t">
          <button type="submit" disabled={processing} className="px-5 py-2.5 text-white rounded-lg" style={{ backgroundColor: '#ED1C24' }}>Create Project</button>
        </div>
      </form>
    </Layout>
  )
}
